import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Save, User, KeyRound, Camera, Globe, ImageIcon, Stethoscope, Plus, X } from "lucide-react";
import InstallAppButton from "../components/InstallAppButton";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", license_number: "", clinic_name: "", clinic_address: "", codice_fiscale: "", partita_iva: "", specialization: "", public_bio: "", is_public: false });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [changing, setChanging] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [newTreatment, setNewTreatment] = useState("");
  const [specKey, setSpecKey] = useState("");
  const [savingSpec, setSavingSpec] = useState(false);
  const photoRef = useRef();
  const logoRef = useRef();

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      license_number: user.license_number || "",
      clinic_name: user.clinic_name || "",
      clinic_address: user.clinic_address || "",
      codice_fiscale: user.codice_fiscale || "",
      partita_iva: user.partita_iva || "",
      specialization: user.specialization || "",
      public_bio: user.public_bio || "",
      is_public: !!user.is_public,
    });
  }, [user]);

  useEffect(() => {
    api.get("/public/specialties").then((r) => setSpecialties(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setSpecKey(user.specialty_key || "");
      setTreatments(user.treatments || []);
    }
  }, [user?.specialty_key, user?.treatments]);

  async function onSpecialtyChange(key) {
    setSpecKey(key);
    setSavingSpec(true);
    try {
      // Backend auto-populates defaults if this is a new specialty
      const { data } = await api.put("/auth/profile", { specialty_key: key });
      setUser(data);
      setTreatments(data.treatments || []);
      toast.success(t("profile.specialtyUpdated"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingSpec(false); }
  }

  async function saveTreatments(next) {
    setTreatments(next);
    try {
      const { data } = await api.put("/auth/profile", { treatments: next });
      setUser(data);
    } catch (err) { toast.error(apiError(err)); }
  }

  function addTreatment(e) {
    if (e && e.preventDefault) e.preventDefault();
    const t = newTreatment.trim();
    if (!t) return;
    if (treatments.some((x) => (x.name || x).toLowerCase() === t.toLowerCase())) {
      toast.error("Ya está en la lista");
      return;
    }
    const defaultDur = user?.slot_duration || 30;
    saveTreatments([...treatments, { name: t, duration_min: defaultDur }]);
    setNewTreatment("");
  }

  function removeTreatment(name) {
    saveTreatments(treatments.filter((x) => (x.name || x) !== name));
  }

  function updateTreatmentDuration(name, duration) {
    // Called on blur only. Accept any positive minute value (1–1440).
    // If the user left the field empty, keep the previous duration instead of resetting.
    const parsed = parseInt(duration);
    saveTreatments(treatments.map((x) => {
      const xname = typeof x === "string" ? x : x.name;
      const prevDur = typeof x === "string" ? (user?.slot_duration || 30) : (x.duration_min || 30);
      if (xname !== name) {
        return typeof x === "string" ? { name: x, duration_min: user?.slot_duration || 30 } : x;
      }
      const dur = Number.isFinite(parsed) && parsed > 0 ? Math.min(1440, parsed) : prevDur;
      return { name, duration_min: dur };
    }));
  }

  async function save(e) {
    e.preventDefault();
    if (form.is_public && (!form.specialization || !user?.photo_url)) {
      toast.error(t("profile.publicRequiresPhotoAndSpec"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  }

  async function onPhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/auth/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, photo_url: data.photo_url });
      toast.success(t("profile.photoUploaded"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadingPhoto(false); e.target.value = ""; }
  }

  async function removePhoto() {
    if (!window.confirm(t("profile.removePhotoConfirm"))) return;
    try {
      await api.delete("/auth/profile/photo");
      setUser({ ...user, photo_url: null });
      toast.success(t("common.deleted"));
    } catch (err) { toast.error(apiError(err)); }
  }

  async function onLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/auth/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, logo_url: data.logo_url });
      toast.success(t("profile.logoUploaded"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadingLogo(false); e.target.value = ""; }
  }

  async function removeLogo() {
    if (!window.confirm(t("profile.removeLogoConfirm"))) return;
    try {
      await api.delete("/auth/profile/logo");
      setUser({ ...user, logo_url: null });
      toast.success(t("common.deleted"));
    } catch (err) { toast.error(apiError(err)); }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (pwd.new_password.length < 6) {
      toast.error(t("profile.pwdTooShort"));
      return;
    }
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error(t("profile.pwdMismatch"));
      return;
    }
    setChanging(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success(t("profile.pwdChanged"));
      setPwd({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) { toast.error(apiError(err)); }
    finally { setChanging(false); }
  }

  const F = (k, label, type = "text", extra = {}) => (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}</label>
      <input {...extra} type={type} value={form[k]} data-testid={`profile-${k}`}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
    </div>
  );

  return (
    <div className="space-y-8 fade-in-up max-w-3xl" data-testid="profile-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("profile.eyebrow")}</p>
        <h1 className="font-heading text-5xl font-light">{t("profile.title")}</h1>
        <p className="text-stone-500 mt-2">{t("profile.subtitle")}</p>
      </div>

      {user?.slug !== undefined && (
        <div className="bg-white border border-stone-200 rounded-2xl p-4 text-center text-xs text-stone-500" data-testid="portal-hint">
          {t("profile.portalMovedHint")}
        </div>
      )}

      <InstallAppButton />

      <form onSubmit={save} className="bg-white border border-stone-200 rounded-2xl p-8 space-y-5">
        <div className="flex items-center gap-4 pb-4 border-b border-stone-100">
          <div className="relative">
            {user?.photo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${user.photo_url}`} alt=""
                className="w-20 h-20 rounded-full object-cover border-2 border-[#D4A373]/40" data-testid="profile-photo-img" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#D4A373]/20 flex items-center justify-center">
                <User className="w-8 h-8 text-[#B07D4F]" strokeWidth={1.5} />
              </div>
            )}
            <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhotoChange} />
            <button type="button" onClick={() => photoRef.current.click()} disabled={uploadingPhoto}
              data-testid="profile-photo-upload"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-stone-900 text-white flex items-center justify-center hover:bg-stone-700 disabled:opacity-50">
              <Camera className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1">
            <div className="font-medium">{user?.email}</div>
            <div className="text-xs text-stone-500">{user?.role === "owner" ? "Owner · Lifetime" : "Doctor"}</div>
            {user?.photo_url && (
              <button type="button" onClick={removePhoto} data-testid="profile-photo-remove"
                className="text-xs text-red-500 hover:text-red-700 underline underline-offset-2 mt-1">
                {t("profile.removePhoto")}
              </button>
            )}
          </div>
        </div>
        <div className="pb-4 border-b border-stone-100">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3 block">{t("profile.clinicLogo")}</label>
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 rounded-xl bg-[#FAF9F6] border border-stone-200 flex items-center justify-center overflow-hidden shrink-0">
              {user?.logo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${user.logo_url}`} alt="logo"
                  className="max-w-full max-h-full object-contain" data-testid="profile-logo-img" />
              ) : (
                <ImageIcon className="w-8 h-8 text-stone-300" strokeWidth={1.2} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-stone-500 leading-relaxed">{t("profile.clinicLogoHint")}</p>
              <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onLogoChange} />
              <div className="flex flex-wrap gap-2 mt-3">
                <button type="button" onClick={() => logoRef.current.click()} disabled={uploadingLogo}
                  data-testid="profile-logo-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-50">
                  <ImageIcon className="w-4 h-4" /> {uploadingLogo ? t("common.loading") : (user?.logo_url ? t("profile.replaceLogo") : t("profile.uploadLogo"))}
                </button>
                {user?.logo_url && (
                  <button type="button" onClick={removeLogo} data-testid="profile-logo-remove"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 text-stone-600 text-sm hover:bg-stone-50">
                    {t("profile.removeLogo")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="pb-6 border-b border-stone-100">
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
            <label className="text-xs uppercase tracking-[0.2em] text-stone-700">{t("profile.specialtyPickerTitle")}</label>
          </div>
          <p className="text-xs text-stone-500 mb-3 leading-relaxed">{t("profile.specialtyPickerHint")}</p>
          <select value={specKey} onChange={(e) => onSpecialtyChange(e.target.value)}
            data-testid="profile-specialty-key" disabled={savingSpec}
            className="w-full md:w-auto px-4 py-2.5 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]">
            <option value="">— {t("profile.selectSpecialty")} —</option>
            {Object.entries(specialties.reduce((acc, s) => {
              (acc[s.group] = acc[s.group] || []).push(s); return acc;
            }, {})).map(([group, items]) => (
              <optgroup key={group} label={group}>
                {items.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </optgroup>
            ))}
          </select>

          {specKey && (
            <div className="mt-5">
              <label className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2 block">{t("profile.treatmentsTitle")}</label>
              <p className="text-xs text-stone-500 mb-3">{t("profile.treatmentsHint")}</p>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="treatments-list">
                {treatments.length === 0 ? (
                  <p className="text-xs text-stone-400 italic">{t("profile.treatmentsEmpty")}</p>
                ) : (
                  treatments.map((tt) => {
                    const name = typeof tt === "string" ? tt : tt.name;
                    const dur = typeof tt === "string" ? (user?.slot_duration || 30) : (tt.duration_min || 30);
                    return (
                      <TreatmentChip key={name} name={name} duration={dur}
                        onCommit={(d) => updateTreatmentDuration(name, d)}
                        onRemove={() => removeTreatment(name)} />
                    );
                  })
                )}
              </div>
              <div className="flex gap-2">
                <input value={newTreatment} onChange={(e) => setNewTreatment(e.target.value)}
                  placeholder={t("profile.addTreatmentPh")} data-testid="new-treatment-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTreatment(); } }}
                  className="flex-1 px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#D4A373]" />
                <button type="button" onClick={addTreatment} data-testid="add-treatment-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-900 text-white text-sm hover:bg-stone-700">
                  <Plus className="w-3.5 h-3.5" /> {t("profile.addTreatmentBtn")}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {F("name", t("profile.name"))}
          {F("specialization", t("profile.specialization"), "text", { placeholder: t("profile.specializationPh") })}
          {F("license_number", t("profile.licenseNumber"))}
          {F("codice_fiscale", t("profile.cf"))}
          {F("partita_iva", t("profile.pi"))}
          {F("clinic_name", t("profile.clinicName"))}
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("profile.clinicAddress")}</label>
          <textarea rows={2} value={form.clinic_address} data-testid="profile-clinic_address"
            onChange={(e) => setForm({ ...form, clinic_address: e.target.value })}
            className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("profile.publicBio")}</label>
          <textarea rows={3} value={form.public_bio} data-testid="profile-public_bio" maxLength={400}
            placeholder={t("profile.publicBioPlaceholder")}
            onChange={(e) => setForm({ ...form, public_bio: e.target.value })}
            className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
          <p className="text-[10px] text-stone-400 mt-1">{form.public_bio.length}/400</p>
        </div>
        <label className="flex items-start gap-3 p-4 rounded-xl bg-[#FAF9F6] border border-stone-200 cursor-pointer hover:border-[#D4A373]" data-testid="is-public-toggle">
          <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
            data-testid="profile-is_public" className="mt-1 w-4 h-4 accent-[#D4A373]" />
          <div className="flex-1">
            <div className="flex items-center gap-2 font-medium text-sm">
              <Globe className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} /> {t("profile.publicVisibility")}
            </div>
            <p className="text-xs text-stone-500 mt-1">{t("profile.publicVisibilityDesc")}</p>
          </div>
        </label>
        <p className="text-xs text-stone-500 leading-relaxed">{t("profile.hint")}</p>
        <button type="submit" disabled={saving} data-testid="profile-save"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? t("common.loading") : t("common.save")}
        </button>
      </form>

      <form onSubmit={changePassword} className="bg-white border border-stone-200 rounded-2xl p-8 space-y-5" data-testid="change-password-form">
        <div className="flex items-center gap-3 pb-4 border-b border-stone-100">
          <div className="w-12 h-12 rounded-full bg-[#8FA08B]/20 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-[#8FA08B]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading text-xl font-light">{t("profile.changePassword")}</div>
            <div className="text-xs text-stone-500">{t("profile.changePasswordHint")}</div>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("profile.currentPassword")}</label>
            <input type="password" value={pwd.current_password} autoComplete="current-password" data-testid="pwd-current"
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("profile.newPassword")}</label>
            <input type="password" value={pwd.new_password} autoComplete="new-password" data-testid="pwd-new"
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("profile.confirmPassword")}</label>
            <input type="password" value={pwd.confirm_password} autoComplete="new-password" data-testid="pwd-confirm"
              onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })}
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#8FA08B]" />
          </div>
        </div>
        <button type="submit" disabled={changing || !pwd.current_password || !pwd.new_password} data-testid="pwd-submit"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#8FA08B] text-white hover:opacity-90 disabled:opacity-50">
          <KeyRound className="w-4 h-4" /> {changing ? t("common.loading") : t("profile.updatePassword")}
        </button>
      </form>
    </div>
  );
}

// Editable treatment chip — buffers the duration in local state while typing,
// commits to the parent only on blur / Enter. Uses type="text" + inputMode="numeric"
// so users can freely delete and retype any number without browser-number-input quirks.
function TreatmentChip({ name, duration, onCommit, onRemove }) {
  const [val, setVal] = useState(String(duration ?? 30));
  const focusedRef = useRef(false);
  // Only sync from parent when the input is NOT focused, to avoid overwriting user typing.
  useEffect(() => {
    if (!focusedRef.current) setVal(String(duration ?? 30));
  }, [duration]);
  function commit() {
    focusedRef.current = false;
    // If user left it empty, restore the previous prop value visually.
    if (val === "" || val === null || val === undefined) {
      setVal(String(duration ?? 30));
      return;
    }
    onCommit(val);
  }
  return (
    <div data-testid={`treatment-chip-${name}`}
      className="inline-flex items-center gap-2 pl-3.5 pr-1.5 py-1.5 rounded-full bg-white border border-stone-200 text-sm text-stone-700 hover:border-[#D4A373]">
      <span>{name}</span>
      <span className="flex items-center gap-1 text-xs text-stone-500 border-l border-stone-200 pl-2 ml-1">
        <input type="text" inputMode="numeric" pattern="[0-9]*" value={val}
          onFocus={(e) => { focusedRef.current = true; e.currentTarget.select(); }}
          onChange={(e) => setVal(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
          data-testid={`treatment-dur-${name}`}
          className="w-14 px-1 py-0.5 bg-[#FAF9F6] border border-stone-200 rounded text-center tabular-nums focus:outline-none focus:border-[#D4A373]" />
        <span className="uppercase tracking-widest text-[9px]">min</span>
      </span>
      <button type="button" onClick={onRemove}
        data-testid={`treatment-remove-${name}`}
        className="w-5 h-5 rounded-full flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors">
        <X className="w-3 h-3" strokeWidth={2.5} />
      </button>
    </div>
  );
}

